/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */
import { FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { GDPRDialog } from '@/components/content/Footer/parts/GDPRDialog';
import { footerContainerSX } from '@/components/content/Footer/styles/container';
import { useModalPrivacyPolicy } from '@/data/Content/ModalPrivacyPolicy';
import { dFix, useSettings } from '@/data/Settings';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { ID } from '@/data/types/Basic';
import { Box, Container, Paper, Stack } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { footerHippoImage, footerLinksContainerSX, footerNavbarSX } from './styles/footerNavbar';
import { useUser } from '@/data/User';
import { CommonDataDelegatorProps, PageDataDelegatorProps, UserDataDelegatorProps } from '@/data/events/delegators/GACommonData';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { EventsContext } from '@/data/context/events';
import { waitForElement } from '@/utils/waitForElementToLoad';
import { useDeviceType } from '@/data/deviceDetection';
import { RegisterInfoPopup } from '../Register/parts/RegisterInfoPopup';
import { getCookie } from '@/utils/clickAndCollect';
import { footerPaymentsContainerSX } from './styles/footerPayments';
import { footerPrivacyLinksSX } from './styles/footerPrivacyLinks';
import { useNewsLetterSubscription } from '@/data/Content/NewsLetterSubscription';
import UserInactivityAlert from '../UserInactivityAlert';
import { Img } from '@/components/blocks/MaterialImage';
import { usePathname } from 'next/navigation';
import { CHECKOUT_PAGE_URLS } from '@/data/constants/checkout';

export const Footer: FC<{
	id: ID;
	clickAction?: () => void;
}> = ({ id }) => {
	const {isMobile} = useDeviceType();
	const { settings } = useSettings();
	const { data, loading, onSubmit, privacyNoticeVersion, privacyNoticeVersionValue } =
		useModalPrivacyPolicy();
	const { user } = useUser();
	let loggedIn = user?.isLoggedIn ?? false;
	const pageDataValue = usePageDataFromId(); 
	const router = useNextRouter();
	const { onCommonView, onXPageView, onUserData } = useContext(EventsContext);
	const DEFAULT_INACTIVITY_TIMEOUT = 1500000; // 25 minutes
	const { locale, query:{flow} } = router;
	const pathName = usePathname()??"";
	const checkoutFlow = CHECKOUT_PAGE_URLS.includes(flow as string) || CHECKOUT_PAGE_URLS.includes(pathName);

	const { userSubscriptionSubmit } = useNewsLetterSubscription();
	
	useEffect(() => {
		if (pageDataValue.data && settings) {
			const commonData: CommonDataDelegatorProps = {
				gtm: {
					storeId: settings.storeId,
					country: "NL", //TBD Read from somewhere
					currency: settings.defaultCurrency,
					environment: settings.userData.GTMEnvironment,
					locale: locale ?? "nl-NL",
					language: settings.defaultLanguage,
					device: isMobile ? "mobile" : "desktop",
					settings,
				}
			};
			const pageData: PageDataDelegatorProps = {
				gtm: {
					pageName: pageDataValue.data?.page.title ?? "",
					pageType: pageDataValue.data?.page.type ?? "",
					settings,
				}
			};
			const userData: UserDataDelegatorProps = {
				gtm: {
					userId: user?.userId ?? "-1002",
					userType: "", // TBD Determine Type. user?.nothingUsefulExistsNow,
					settings,
				}
			};
			onCommonView(commonData);
			onUserData(userData);
			onXPageView(pageData);
		}
	}, [pageDataValue, settings, onUserData]);

	useEffect(() => {
		let headerElems: NodeListOf<Element> | null = null;
		const handleLinkHeaderClick = (event: any) => {
			if (headerElems) {
				[...headerElems].forEach(elem => {
					elem.classList.remove("selected");
					if (event.target !== elem) {
						elem.nextElementSibling?.classList.add("hide-mobile");
					}
				});
				const targetList = event.target.nextElementSibling;
				if (targetList && targetList.classList) {
					if ([...targetList.classList].includes("hide-mobile")) {
						targetList.classList.remove("hide-mobile");
						targetList.classList.add("selected");
						event.target.classList.add("selected");
						event.target.scrollIntoView(true, { behavior: "smooth" });
						window.scrollBy(0, -50);
					} else {
						targetList.classList.add("hide-mobile");
					}
				}
			}
		};
		if (isMobile) {
			waitForElement('.footer-links-header', 4000, 'footer').then(function () {
				headerElems = document.querySelectorAll(".footer-links-header");
				[...headerElems].forEach(elem => {
					elem.addEventListener('click', handleLinkHeaderClick, false);
				});
			}).catch((error) => {
				console.debug(error.message);
			});
		}
		return () => {
			if (headerElems) {
				[...headerElems].forEach(elem => {
					elem.removeEventListener('click', handleLinkHeaderClick, false);
				});
			}
		};
	}, [isMobile]);

	const onUserSubscriptionSubmit = useCallback(async()=>{
		const emailElem = document.querySelector<HTMLInputElement>("#news_letter_user_email");
		if(emailElem){
			const emailErrorElem = document.querySelector<HTMLElement>("#news_letter_user_email_error");
			if(emailErrorElem){
				emailErrorElem.style.display = "none";
				emailErrorElem.role = "alert";
			}
			emailElem?.classList.remove("error");
			if(emailElem.checkValidity()){
				await userSubscriptionSubmit({emailId : emailElem.value});
				emailElem.value = "";
			}else{
				emailElem?.classList.add("error");
				if(emailErrorElem){
					emailErrorElem.style.display = "block";
					emailErrorElem.role = "alert";
				}
			}
		}
	},[]);

	useEffect(()=>{
		let footerNewsLetterSubmitBtn:HTMLButtonElement|null = null;
		waitForElement('#NewsletterSubscription', 4000, 'footer').then(()=>{
			footerNewsLetterSubmitBtn = document.querySelector("#NewsletterSubscription");
			if(footerNewsLetterSubmitBtn){
				footerNewsLetterSubmitBtn?.addEventListener("click",onUserSubscriptionSubmit);
			}
		}).catch((error) => {
			console.debug(error.message);
		});
		
		return ()=>{
			if(footerNewsLetterSubmitBtn){
				footerNewsLetterSubmitBtn?.removeEventListener("click", onUserSubscriptionSubmit);
			}
		}
		
	},[]);

	const acceptedSessionPrivacyPolicy =
		dFix(privacyNoticeVersion ?? '') === dFix(privacyNoticeVersionValue ?? '');

	const [isUserAccRegSubmitted, setUserAccRegSubmitted] = useState(false);
	useEffect(()=>{ // useEffect needed as getCookie wont works in SSR
		setUserAccRegSubmitted(getCookie("USER_ACCOUNT_REGISTRATION_SUBMITTED") === 'true');
	},[pageDataValue?.data?.identifier]);

	return (
		<Paper
			elevation={0}
			component="footer"
			sx={footerContainerSX(settings?.csrSession,checkoutFlow)}
			{...(settings?.csrSession && { 'data-iframe-height': '' })}
		>
			<Box className="arc-wrapper">
				{checkoutFlow?		 
					<Img sx={footerHippoImage} alt="checkout_footerHippoImage" id ="checkout_footerHippoImage" src={`${settings.userData.StaticHostSecure}/wcsstore/IntertoysSAS/images/colors/color1/footer/hippo-checkout.png`} /> 
				:null}
				<svg viewBox="0 0 1440 200" preserveAspectRatio="none" aria-hidden="true">
					<path d="M0,60 C360,140 1080,-20 1440,60 L1440,200 L0,200 Z" fill="#ffdc00" />
				</svg>	
			</Box>
			
			<Box sx={{ bgcolor: 'navbar.primary', position:'relative', zIndex:1, mt:checkoutFlow?0:-5  }}> {/* marginTop:checkoutFlow?'30px':'0px' */}
			{!checkoutFlow?		
			<Container> 
					<Box sx={footerLinksContainerSX}>
					<ContentRecommendation
							id='IT-Footer-Links-eSpot_rv'
							properties={{emsName:'IT-Footer-Links-eSpot_rv'}}
						/>
					</Box>					
				</Container>
				 :/*  <CheckoutFooter id="id"/> */
			null
			}
			</Box>
			
			<Box sx={{borderBottom:'1px solid #d5d5d5'}}>
				<Container>
					<Box sx={footerPaymentsContainerSX}> 
						<ContentRecommendation
							id='IT-Footer-Payment-eSpot_rv'
							properties={{emsName:'IT-Footer-Payment-eSpot_rv'}}
						/>
					</Box>
				</Container>
			</Box>
			<Box>
				<Container>
					<Box sx={footerPrivacyLinksSX}>
						<ContentRecommendation 
							id="IT-Footer-Privacy-Links-espot-rv"
							properties={{emsName:"IT-Footer-Privacy-Links-espot-rv"}}
						/>
					</Box>
				</Container>
			</Box>
			{!acceptedSessionPrivacyPolicy ? (
				<FlowIfEnabled feature={EMS_STORE_FEATURE.CONSENT_OPTIONS}>
					{loading ? <ProgressIndicator /> : <GDPRDialog data={data} onSubmit={onSubmit} />}
				</FlowIfEnabled>
			) : null}
			{isUserAccRegSubmitted ? <RegisterInfoPopup /> : null}
			<Box id="empty_space_place_holder"></Box>
			{loggedIn ? <UserInactivityAlert timeOut={settings?.userData.UserInactivityTimeAlert ?? DEFAULT_INACTIVITY_TIMEOUT} /> : null}
			<Box display="none">
				<ContentRecommendation
					id='IT-HTML-Banner-Styles-eSpot_rv'
					properties={{emsName:'IT-HTML-Banner-Styles-eSpot_rv'}}
				/>
			</Box>
		</Paper>
	);
};